import React, { useRef, useEffect, useState, useCallback } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPin } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Route Polyline — tries new Route API, falls back to legacy DirectionsService
const RoutePolyline = ({ origin, destination }) => {
  const map = useMap();
  const routesLibrary = useMapsLibrary('routes');
  const polylinesRef = useRef([]);

  useEffect(() => {
    if (!map || !routesLibrary || !origin || !destination) return;

    let active = true;
    const orig = { lat: origin.latitude, lng: origin.longitude };
    const dest = { lat: destination.latitude, lng: destination.longitude };

    const cleanup = () => {
      polylinesRef.current.forEach(p => p.setMap(null));
      polylinesRef.current = [];
    };

    const drawPolyline = (path) => {
      if (!active) return;
      cleanup();
      const polyline = new google.maps.Polyline({
        path, strokeColor: '#EC4899', strokeWeight: 4, strokeOpacity: 0.8, map
      });
      polylinesRef.current = [polyline];
    };

    const computeRoute = async () => {
      // Try new Route API
      if (routesLibrary.Route) {
        try {
          const { routes } = await routesLibrary.Route.computeRoutes({
            origin: orig, destination: dest, travelMode: 'DRIVING', fields: ['path'],
          });
          if (routes?.length && routes[0].path) {
            drawPolyline(routes[0].path);
            return;
          }
        } catch (err) {
          console.warn('Routes API unavailable, using legacy Directions API:', err.message);
        }
      }

      // Fallback: legacy DirectionsService
      const service = new routesLibrary.DirectionsService();
      service.route({ origin: orig, destination: dest, travelMode: google.maps.TravelMode.DRIVING }, (result, status) => {
        if (!active || status !== google.maps.DirectionsStatus.OK || !result) return;
        drawPolyline(result.routes[0].overview_path);
      });
    };

    computeRoute();
    return () => { active = false; cleanup(); };
  }, [map, routesLibrary, origin, destination]);

  return null;
};

// Map Bounds Fitter Component
const MapBoundsFitter = ({ pickup, dropoff, driverLocation }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    if (pickup) {
      bounds.extend({ lat: pickup.latitude, lng: pickup.longitude });
      hasPoints = true;
    }
    if (dropoff) {
      bounds.extend({ lat: dropoff.latitude, lng: dropoff.longitude });
      hasPoints = true;
    }
    if (driverLocation) {
      bounds.extend({ lat: driverLocation.latitude, lng: driverLocation.longitude });
      hasPoints = true;
    }

    if (hasPoints) {
      // Small delay to ensure map is ready
      setTimeout(() => {
        map.fitBounds(bounds, { padding: 60 });
      }, 100);
    }
  }, [map, pickup, dropoff, driverLocation]);

  return null;
};

const MapView = ({ 
  pickup, 
  dropoff, 
  route, 
  driverLocation,
  onMapLoad,
  showRoute = true,
  height = '100%',
  className = ''
}) => {
  const mapRef = useRef();
  
  // Check if API key is available
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`} style={{ height }}>
        <div className="text-center p-6">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Map not available</p>
          <p className="text-sm text-gray-500 mt-1">Google Maps API key not configured</p>
        </div>
      </div>
    );
  }

  // Calculate initial center - prioritize Zambia coordinates
  const getInitialCenter = () => {
    if (pickup) {
      return { lat: pickup.latitude, lng: pickup.longitude };
    }
    if (dropoff) {
      return { lat: dropoff.latitude, lng: dropoff.longitude };
    }
    // Default to Kasama, Zambia
    return { lat: -10.2129, lng: 31.1802 };
  };

  return (
    <div className={className} style={{ height, width: '100%' }}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          ref={mapRef}
          defaultZoom={12}
          defaultCenter={getInitialCenter()}
          mapId="tekstar_ride_map"
          gestureHandling="greedy"
          disableDefaultUI={false}
          zoomControl={true}
          streetViewControl={false}
          mapTypeControl={false}
          fullscreenControl={false}
          style={{ width: '100%', height: '100%', borderRadius: '0.5rem' }}
        >
          {/* Fit bounds to markers */}
          <MapBoundsFitter pickup={pickup} dropoff={dropoff} driverLocation={driverLocation} />

          {/* Pickup Marker */}
          {pickup && (
            <AdvancedMarker position={{ lat: pickup.latitude, lng: pickup.longitude }}>
              <div className="relative">
                <div className="w-8 h-8 bg-gray-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                  <div className="bg-white px-2 py-1 rounded shadow text-xs font-medium">
                    Pickup
                  </div>
                </div>
              </div>
            </AdvancedMarker>
          )}

          {/* Dropoff Marker */}
          {dropoff && (
            <AdvancedMarker position={{ lat: dropoff.latitude, lng: dropoff.longitude }}>
              <div className="relative">
                <div className="w-8 h-8 bg-pink-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                  <div className="bg-white px-2 py-1 rounded shadow text-xs font-medium">
                    Dropoff
                  </div>
                </div>
              </div>
            </AdvancedMarker>
          )}

          {/* Driver Marker - Real-time location */}
          {driverLocation && (
            <AdvancedMarker position={{ lat: driverLocation.latitude, lng: driverLocation.longitude }}>
              <div className="relative animate-pulse">
                <div className="w-10 h-10 bg-blue-600 rounded-full border-3 border-white shadow-xl flex items-center justify-center">
                  <div className="text-white text-xl">🚗</div>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-blue-400 rounded-full opacity-30 animate-ping"></div>
              </div>
            </AdvancedMarker>
          )}

          {/* Route Line */}
          {showRoute && pickup && dropoff && (
            <RoutePolyline origin={pickup} destination={dropoff} />
          )}
        </Map>
      </APIProvider>
    </div>
  );
};

export default MapView;
